// src/portal-app/shell/kit/ProfileHero.js
//
// The P5 identity block. A detail screen without one could be about anyone —
// that anonymity was half of why Employee Detail read as unreadable (spec §1 #4).
//
// Quick actions are capped at three — spec §5 P5's limit, not a layout
// constraint. (.pk-prof-action is flex:1 1 0 with a fixed min-height:44px,
// so a fourth action would not shrink below the touch minimum; the cap
// exists because the spec says three, full stop.)
//
// `testId` on an action item is optional and additive (Plan 4 Task 18 —
// PayRunDetailScreen), exactly the precedent TabPills already set for
// preserving legacy testids across a redesign: PayRunDetailScreen's
// status-driven mutation buttons (Refresh/Finalize/Re-finalize/Pay All/
// Close/Reopen) carry pre-existing `action-*` testids that its own and a
// sibling regression test file (PayRunDetailScreen.confirmSheet.test.js)
// assert on directly, so moving them into ProfileHero's action row without
// a way to keep those testids would silently break both files.
import React from 'react';
import { GlassCard } from '../LiquidGlass';
import { ensureKitCss } from './kitCss';
import { toneVar } from './tones';

const MAX_ACTIONS = 3;

export function ProfileHero({ initials, tone = 'blue', name, subtitle, pill, actions }) {
    ensureKitCss();
    const token = toneVar(tone);
    const shown = Array.isArray(actions) ? actions.slice(0, MAX_ACTIONS) : [];

    return (
        <GlassCard veil className="pk-prof" data-testid="profile-hero">
            <div className="pk-prof-top">
                <span
                    className="pk-prof-avatar"
                    style={{ background: `var(${token})` }}
                    aria-hidden="true"
                >
                    {initials}
                </span>
                <div className="pk-prof-id">
                    <div className="pk-prof-name">{name}</div>
                    {subtitle && <div className="pk-prof-sub">{subtitle}</div>}
                    {pill && (
                        <span className={`pk-pill pk-pill-${pill.tone || 'muted'} pk-prof-pill`}>
                            {pill.label}
                        </span>
                    )}
                </div>
            </div>

            {shown.length > 0 && (
                <div className="pk-prof-actions">
                    {shown.map((a) => (
                        <button
                            key={a.id}
                            type="button"
                            className="pk-prof-action"
                            onClick={a.onPress}
                            data-testid={a.testId || undefined}
                        >
                            {a.icon && <span className="pk-prof-action-icon" aria-hidden="true">{a.icon}</span>}
                            <span>{a.label}</span>
                        </button>
                    ))}
                </div>
            )}
        </GlassCard>
    );
}

export default ProfileHero;
